import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import Button from '../../components/ui/Button';
import '../../styles/RolesPage.css';

const ALL_PERMISSIONS_LABELS_KEY = {
  'users.view':       'users.view',
  'users.edit':       'users.edit',
  'users.delete':     'users.delete',
  'users.invite':     'users.invite',
  'roles.view':       'roles.view',
  'roles.edit':       'roles.edit',
  'audit.view':       'audit.view',
  'sessions.view':    'sessions.view',
  'sessions.revoke':  'sessions.revoke',
  'stats.view':       'stats.view',
};

const ALL_PERMISSIONS_LABELS = {
  'users.view':           'View Users',
  'users.edit':           'Edit Users',
  'users.delete':         'Delete Users',
  'users.invite':         'Send Invitations',
  'roles.view':           'View Roles',
  'roles.edit':           'Edit Roles',
  'audit.view':           'Audit Log',
  'sessions.view':        'View Sessions',
  'sessions.revoke':      'Revoke Sessions',
  'stats.view':           'Statistics',
  'cases.view_all':       'View All Cases',
  'cases.manage':         'Manage Cases',
  'cases.assign':         'Assign Cases',
  'cases.internal_note':  'Internal Notes',
};

const PERM_CATEGORIES = {
  users:    ['users.view','users.edit','users.delete','users.invite'],
  roles:    ['roles.view','roles.edit'],
  cases:    ['cases.view_all','cases.manage','cases.assign','cases.internal_note'],
  audit:    ['audit.view'],
  sessions: ['sessions.view','sessions.revoke'],
  stats:    ['stats.view'],
};

const CATEGORY_ICONS = {
  users: '👥', roles: '🎭', cases: '📂', audit: '📋', sessions: '🖥️', stats: '📊',
};

const RolesPage = () => {
  const { api } = useAuth();
  const { t } = useI18n();

  const [roles, setRoles]   = useState([]);
  const [perms, setPerms]   = useState([]);
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState('');
  const [msg, setMsg]       = useState('');

  const [newName, setNewName]   = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newBg, setNewBg]       = useState('#e2e8f0');
  const [newColor, setNewColor] = useState('#4a5568');
  const [creating, setCreating] = useState(false);

  const [editingRole, setEditingRole] = useState(null);
  const [editPerms, setEditPerms]     = useState(new Set());
  const [savingPerms, setSavingPerms] = useState(false);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const [rRes, pRes] = await Promise.all([
        api.get('/admin/roles'),
        api.get('/admin/permissions'),
      ]);
      setRoles(rRes.data.roles);
      setPerms(pRes.data.permissions);
    } catch (e) {
      setError(e.response?.data?.error || t('common.error'));
    } finally { setLoad(false); }
  }, [api, t]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true); setMsg(''); setError('');
    try {
      await api.post('/admin/roles', {
        name: newName, label: newLabel,
        color_bg: newBg, color_text: newColor,
      });
      setMsg(`✅ ${t('admin.roleCreated')}`);
      setNewName(''); setNewLabel('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
    } finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.confirmDeleteRole'))) return;
    try {
      await api.delete(`/admin/roles/${id}`);
      setRoles(r => r.filter(x => x.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || t('admin.deleteFailed'));
    }
  };

  const openPermEditor = (role) => {
    setEditingRole(role.id);
    setEditPerms(new Set(role.permissions || []));
  };

  const savePerms = async () => {
    setSavingPerms(true);
    try {
      await api.put(`/admin/roles/${editingRole}/permissions`, {
        permissions: [...editPerms],
      });
      setMsg(`✅ ${t('admin.permissionsSaved')}`);
      setEditingRole(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.saveFailed'));
    } finally { setSavingPerms(false); }
  };

  const togglePerm = (name) => setEditPerms(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const editingRoleObj = roles.find(r => r.id === editingRole);

  return (
    <div className="roles-page">
      <h1 className="roles-title">🎭 {t('admin.rolesAndPermissions')}</h1>

      {msg   && <p className="roles-msg success">{msg}</p>}
      {error && <p className="roles-msg error">❌ {error}</p>}

      {/* CREATE FORM */}
      <div className="roles-card">
        <h3>{t('admin.createRole')}</h3>
        <form onSubmit={handleCreate} className="roles-form">
          <input className="roles-input" placeholder={t('admin.roleIdentifier')} value={newName}
            onChange={e => setNewName(e.target.value)} required />
          <input className="roles-input" placeholder={t('admin.roleDisplayName')} value={newLabel}
            onChange={e => setNewLabel(e.target.value)} required />
          <div className="roles-color-group">
            <label>{t('admin.background')}</label>
            <input type="color" value={newBg} onChange={e => setNewBg(e.target.value)} className="roles-color" />
            <label>{t('admin.text')}</label>
            <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} className="roles-color" />
            <span className="roles-preview" style={{ background: newBg, color: newColor }}>
              {newLabel || t('admin.preview')}
            </span>
          </div>
          <Button type="submit" variant="solid" disabled={creating}>
            {creating ? t('admin.creating') : `+ ${t('admin.create')}`}
          </Button>
        </form>
      </div>

      {/* ROLES LIST */}
      <div className="roles-card">
        <h3>{t('admin.existingRoles')}</h3>
        {loading ? <p className="roles-empty">{t('common.loading')}</p>
          : roles.length === 0 ? <p className="roles-empty">{t('admin.noRoles')}</p>
          : (
          <div className="roles-list">
            {roles.map(role => (
              <div key={role.id} className="role-item">
                <div className="role-left">
                  <span className="role-badge-preview" style={{ background: role.color_bg, color: role.color_text }}>
                    {role.label}
                  </span>
                  <span className="role-name">@{role.name}</span>
                  {role.is_system === 1 && <span className="role-system-tag">{t('admin.system')}</span>}
                </div>
                <div className="role-perms-summary">
                  {(role.permissions || []).length} {t('admin.permissionCount')}
                </div>
                <div className="role-actions">
                  <Button variant="outline" size="sm" onClick={() => openPermEditor(role)}>
                    🔑 {t('admin.permissionsBtn')}
                  </Button>
                  {!role.is_system && (
                    <Button variant="danger" size="sm" onClick={() => handleDelete(role.id)}>
                      🗑️
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PERMISSION EDITOR MODAL */}
      {editingRole && editingRoleObj && (
        <div className="modal-overlay" onClick={() => setEditingRole(null)}>
          <div className="perm-modal" onClick={e => e.stopPropagation()}>
            <h3>
              🔑 {t('admin.permissionsFor')}{' '}
              <span style={{ background: editingRoleObj.color_bg, color: editingRoleObj.color_text,
                padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
                {editingRoleObj.label}
              </span>
            </h3>
            <div className="perm-categories">
              {Object.entries(PERM_CATEGORIES).map(([cat, permNames]) => (
                <div key={cat} className="perm-category">
                  <div className="perm-cat-label">{CATEGORY_ICONS[cat]} {cat}</div>
                  {permNames.map(pname => (
                    <label key={pname} className="perm-row">
                      <input
                        type="checkbox"
                        checked={editPerms.has(pname)}
                        onChange={() => togglePerm(pname)}
                      />
                      <span>{ALL_PERMISSIONS_LABELS[pname] ?? pname}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <div className="perm-modal-actions">
              <Button variant="ghost" onClick={() => setEditingRole(null)}>{t('admin.cancel')}</Button>
              <Button variant="solid" onClick={savePerms} disabled={savingPerms}>
                {savingPerms ? t('admin.saving') : `💾 ${t('admin.save')}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPage;
